/**
 * Utility functions for FaceTec configuration and environment checking
 */

interface ConfigCheckResult {
  isValid: boolean;
  issues: string[];
  warnings: string[];
}

interface EnvironmentInfo {
  isDevelopment: boolean;
  isLocalhost: boolean;
  protocol: string;
  hostname: string;
}

/**
 * Check if the FaceTec SDK configuration is likely valid
 */
export function checkFaceTecConfiguration(): ConfigCheckResult {
  const issues: string[] = [];
  const warnings: string[] = [];

  // Check if running on HTTPS (required for camera access)
  if (
    typeof window !== 'undefined' &&
    window.location.protocol !== 'https:' &&
    window.location.hostname !== 'localhost'
  ) {
    issues.push(
      'HTTPS is required for camera access. FaceTec will not work on HTTP in production.'
    );
  }

  // Check for localStorage availability
  try {
    if (typeof Storage === 'undefined' || !window.localStorage) {
      issues.push('localStorage is not available. This is required for session management.');
    }
  } catch {
    issues.push('localStorage access is restricted. Check browser settings or incognito mode.');
  }

  // Check for sessionStorage availability
  try {
    if (typeof Storage === 'undefined' || !window.sessionStorage) {
      issues.push('sessionStorage is not available. This is required for session management.');
    }
  } catch {
    issues.push('sessionStorage access is restricted. Check browser settings or incognito mode.');
  }

  // Check WebAssembly support
  if (typeof WebAssembly === 'undefined') {
    issues.push('WebAssembly is not supported. This is required for FaceTec SDK.');
  }

  // Check camera access
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    issues.push('Camera access is not available. This is required for biometric verification.');
  }

  // Development environment warnings
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    warnings.push('Running in development environment. Some features may be limited.');
  }

  // Check if running in iframe
  if (window !== window.top) {
    issues.push('FaceTec cannot run inside iframes for security reasons.');
  }

  // Check Content Security Policy compatibility
  try {
    const testScript = document.createElement('script');
    testScript.src = 'data:text/javascript,';
    document.head.appendChild(testScript);
    document.head.removeChild(testScript);
  } catch {
    warnings.push(
      'Content Security Policy may be too restrictive. Configure CSP to allow necessary scripts.'
    );
  }

  return {
    isValid: issues.length === 0,
    issues,
    warnings,
  };
}

/**
 * Get environment information
 */
export function getEnvironmentInfo(): EnvironmentInfo {
  const hostname = window.location.hostname;

  return {
    isDevelopment: process.env.NODE_ENV === 'development',
    isLocalhost:
      hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('.local'),
    protocol: window.location.protocol,
    hostname,
  };
}

/**
 * Check session key availability
 */
export function checkSessionKey(sessionKeyName: string): {
  available: boolean;
  message: string;
} {
  try {
    const sessionKey = sessionStorage.getItem(sessionKeyName);

    if (!sessionKey) {
      return {
        available: false,
        message: 'Session key not found. Please refresh the page or restart the process.',
      };
    }

    if (sessionKey.trim().length === 0) {
      return {
        available: false,
        message: 'Session key is empty. Please refresh the page or restart the process.',
      };
    }

    return {
      available: true,
      message: 'Session key is available.',
    };
  } catch (error) {
    return {
      available: false,
      message: `Error accessing session key: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Generate a comprehensive diagnostic report
 */
export function generateDiagnosticReport(sessionKeyName: string) {
  const configCheck = checkFaceTecConfiguration();
  const envInfo = getEnvironmentInfo();
  const sessionCheck = checkSessionKey(sessionKeyName);

  return {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    environment: envInfo,
    configuration: configCheck,
    sessionKey: sessionCheck,
    capabilities: {
      webAssembly: typeof WebAssembly !== 'undefined',
      mediaDevices: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      localStorage: (() => {
        try {
          return typeof Storage !== 'undefined' && !!window.localStorage;
        } catch {
          return false;
        }
      })(),
      sessionStorage: (() => {
        try {
          return typeof Storage !== 'undefined' && !!window.sessionStorage;
        } catch {
          return false;
        }
      })(),
    },
    recommendations: generateRecommendations(configCheck, envInfo),
  };
}

/**
 * Generate recommendations based on configuration issues
 */
function generateRecommendations(
  configCheck: ConfigCheckResult,
  envInfo: EnvironmentInfo
): string[] {
  const recommendations: string[] = [];

  if (!configCheck.isValid) {
    if (configCheck.issues.some((issue) => issue.includes('HTTPS'))) {
      recommendations.push('Switch to HTTPS for production deployment');
    }

    if (configCheck.issues.some((issue) => issue.includes('localStorage'))) {
      recommendations.push('Check browser settings and disable incognito mode');
    }

    if (configCheck.issues.some((issue) => issue.includes('WebAssembly'))) {
      recommendations.push('Update to a modern browser that supports WebAssembly');
    }

    if (configCheck.issues.some((issue) => issue.includes('Camera'))) {
      recommendations.push('Grant camera permissions and check browser compatibility');
    }

    if (configCheck.issues.some((issue) => issue.includes('iframe'))) {
      recommendations.push('Do not embed FaceTec components inside iframes');
    }
  }

  if (envInfo.isDevelopment) {
    recommendations.push('Test in production environment for full functionality');
  }

  if (configCheck.warnings.length > 0) {
    recommendations.push('Review CSP settings to ensure compatibility');
  }

  return recommendations;
}
